import { Alert, Box, Button, Chip, LinearProgress, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { GovKind, ReleaseMilestones } from "../../../../utils/icGovernance";
import FieldGrid from "../detail/FieldGrid";
import SideCard from "../detail/SideCard";
import { DetailOf } from "../detail/types";
import { FAINT, MONO, TEXT, neutralButtonSx } from "../detail/ui";
import {
  GOVERNANCE_BASE,
  Tone,
  dateOnly,
  dateTime,
  releaseTypeLabel,
  statusLabel,
  statusTone,
  statusesFor,
  toneColors,
} from "../governanceStatus";

const MILESTONES: [keyof ReleaseMilestones, string][] = [
  ["devComplete", "Dev complete"],
  ["qaComplete", "QA complete"],
  ["uatStart", "UAT start"],
  ["uatComplete", "UAT complete"],
  ["production", "Production"],
];

const CountChip = ({ label, count, tone }: { label: string; count: number; tone: Tone }) => {
  const c = toneColors(tone);
  return <Chip size="small" label={`${label} · ${count}`} sx={{ fontWeight: 700, backgroundColor: c.bg, color: c.fg }} />;
};

/** Non-zero counts by status, in the kind's status order. */
const StatusCounts = ({ kind, byStatus }: { kind: GovKind; byStatus: Record<string, number> | undefined }) => {
  const order = statusesFor(kind);
  const rank = (s: string) => (order.indexOf(s) < 0 ? order.length : order.indexOf(s));
  const entries = Object.entries(byStatus ?? {})
    .filter(([, n]) => n > 0)
    .sort(([a], [b]) => rank(a) - rank(b));
  if (entries.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: FAINT }}>
        None
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap" }}>
      {entries.map(([status, n]) => (
        <CountChip key={status} label={statusLabel(status)} count={n} tone={statusTone(kind, status)} />
      ))}
    </Stack>
  );
};

/** Release fields, milestones, contents and the UAT pass count. */
const ReleasePanel = ({ item }: { item: DetailOf<"RELEASE"> }) => {
  const milestones: Partial<ReleaseMilestones> = item.milestones ?? {};
  const summary = item.releaseSummary;
  const uat = summary?.uat;
  const pct = uat && uat.total > 0 ? Math.round((uat.pass / uat.total) * 100) : 0;
  const blocked = !!uat && uat.fail + uat.notRun > 0;

  return (
    <Stack spacing={2}>
      <SideCard title="Release details">
        <FieldGrid
          fields={[
            { label: "Version", value: item.version },
            { label: "Release type", value: releaseTypeLabel(item.releaseType) },
            { label: "Expected downtime", value: item.expectedDowntime },
            { label: "Deployed at", value: item.deployedAt ? dateTime(item.deployedAt) : "Not deployed yet" },
          ]}
        />
        <Typography variant="caption" component="h3" sx={{ display: "block", color: FAINT, fontWeight: 600, mt: 2, mb: 0.75 }}>
          Milestones
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(118px, 1fr))", gap: 1 }}>
          {MILESTONES.map(([key, label]) => (
            <Box key={key} sx={{ border: "1px solid #f0f0f3", borderRadius: "10px", px: 1.25, py: 1 }}>
              <Typography variant="caption" component="p" sx={{ color: FAINT }}>
                {label}
              </Typography>
              <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 13, color: TEXT }}>
                {dateOnly(milestones[key])}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ mt: 2 }}>
          <FieldGrid
            fields={[
              { label: "User communication", value: item.userCommunication, full: true },
              { label: "Rollback plan", value: item.rollbackPlan, full: true },
              { label: "Post-deploy validation", value: item.postDeployValidation, full: true },
            ]}
          />
        </Box>
      </SideCard>

      <SideCard title="Contents">
        {summary ? (
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700, color: TEXT, mb: 0.5 }}>
                Change requests ({summary.changeRequests?.total ?? 0})
              </Typography>
              <StatusCounts kind="CHANGE_REQUEST" byStatus={summary.changeRequests?.byStatus} />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700, color: TEXT, mb: 0.5 }}>
                Issues ({summary.issues?.total ?? 0})
              </Typography>
              <StatusCounts kind="ISSUE" byStatus={summary.issues?.byStatus} />
            </Box>
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ color: FAINT }}>
            No summary available.
          </Typography>
        )}
      </SideCard>

      <SideCard title="UAT">
        {!uat || uat.total === 0 ? (
          <Typography variant="body2" sx={{ color: FAINT }}>
            No UAT tests yet.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" sx={{ fontWeight: 700, color: TEXT }}>
              {uat.pass} of {uat.total} passed ({pct}%)
            </Typography>
            <LinearProgress
              variant="determinate"
              value={pct}
              aria-label={`${pct}% of UAT tests passed`}
              sx={{
                mt: 1,
                height: 8,
                borderRadius: 4,
                backgroundColor: "#f0f0f3",
                "& .MuiLinearProgress-bar": { backgroundColor: "#1e7b3c", borderRadius: 4 },
              }}
            />
            <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap", mt: 1.5 }}>
              <CountChip label="Pass" count={uat.pass} tone="green" />
              <CountChip label="Fail" count={uat.fail} tone="red" />
              <CountChip label="Blocked" count={uat.blocked} tone="red" />
              <CountChip label="Not run" count={uat.notRun} tone="grey" />
            </Stack>
          </>
        )}
        {blocked ? (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            This release cannot be approved until every test passes or is marked blocked.
          </Alert>
        ) : null}
        <Button
          component={RouterLink}
          to={`${GOVERNANCE_BASE}/uat?release=${encodeURIComponent(item.id)}`}
          variant="outlined"
          color="inherit"
          sx={{ ...neutralButtonSx, mt: 1.5 }}
        >
          View tests
        </Button>
      </SideCard>
    </Stack>
  );
};

export default ReleasePanel;
